import React, { useEffect } from 'react'
import { MenuItem, Select, TextareaAutosize } from '@mui/material'
import { InputField } from '../common/InputField'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { commonSelectStyles, commonShadowStyles, commonTextareaStyles } from '../common/InputStyles'
import { ModalFooter } from '../../../../../../common/components/wizard/ModalFooter'
import {
  BOOKING_CONTACT_METHODS,
  BOOKING_CLIENT_TYPES,
  BookingCategory,
  BookingDetailsFields,
  BookingClientType,
} from 'common/types/MemberBooking'
import { FileSelector } from './FileSelector'
import { CorePersonalFieldsPartial, schemaCorePersonalFields } from '../../MemberProfile/CorePersonalFieldsPartial/CorePersonalFieldsPartial'
import { yupResolver } from '@hookform/resolvers/yup'
import { SimpleErrorMessage } from '../common/SimpleErrorMessage'
import * as yup from 'yup'

interface Step2BookingDetailsProps {
  handleStep2Submit: (values: BookingDetailsFields) => void
  bookingCategory: BookingCategory
  initialValues?: BookingDetailsFields
  occupations: { value: string; label: string }[]
  selectedFile?: File
  setSelectedFile: (file: File | null) => void
}

export const Step2BookingDetails = ({
  handleStep2Submit,
  bookingCategory,
  initialValues,
  occupations,
  selectedFile,
  setSelectedFile,
}: Step2BookingDetailsProps) => {
  const schemaStep2BookingDetailsFields = yup.object().shape({
    userNotes: yup.string().optional(),
    contactMethod: yup
      .string()
      .oneOf(BOOKING_CONTACT_METHODS.map((method) => method.value), 'Please select a contact method'),
    occupation: yup.string().required('Please select an occupation'),
    accessibilityRequirements: yup.string().optional(),
    selectedFile: yup.mixed<File>().nullable(),
    fileUrl: yup.string().url('Please enter a valid URL').optional(),
    bookingClientType: yup
      .mixed<BookingClientType>()
      .oneOf(BOOKING_CLIENT_TYPES.map((type) => type.value), 'Please select a valid option')
      .required('Please select an option'),
  })

  const combinedSchema: yup.ObjectSchema<BookingDetailsFields> =
    schemaCorePersonalFields.concat(schemaStep2BookingDetailsFields)

  const methodsRHF = useForm<BookingDetailsFields>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: initialValues?.firstName || '',
      lastName: initialValues?.lastName || '',
      emailAddress: initialValues?.emailAddress || '',
      phoneNumber: initialValues?.phoneNumber || '',
      userNotes: initialValues?.userNotes || '',
      occupation: occupations.find((occupation) => occupation.value === initialValues?.occupation)
        ? initialValues?.occupation
        : '',
      accessibilityRequirements: initialValues?.accessibilityRequirements || '',
      contactMethod: initialValues?.contactMethod || '',
      selectedFile: initialValues?.selectedFile || null,
      fileUrl: initialValues?.fileUrl || '',
      bookingClientType: initialValues?.bookingClientType || null,
    },
    criteriaMode: 'all',
    resolver: yupResolver(combinedSchema),
  })

  const doValidation = async () => {
    const values = methodsRHF.getValues()
    const isValid = await methodsRHF.trigger()

    if (bookingCategory === BookingCategory.DOCUMENT_REVIEW && !values.selectedFile && !values.fileUrl) {
      methodsRHF.setError('fileUrl', {
        type: 'required',
        message: 'Please provide either a file or a URL to a file',
      })
    }
    if (isValid && !methodsRHF.formState.errors.fileUrl) {
      handleStep2Submit({ ...values, selectedFile })
    }
    return isValid
  }

  useEffect(() => {
    if (bookingCategory === BookingCategory.DOCUMENT_REVIEW) {
      methodsRHF.trigger('fileUrl')
    }
  }, [selectedFile, bookingCategory, methodsRHF.trigger])

  const handleFileChange = (file: File | null) => {
    setSelectedFile(file)
    methodsRHF.setValue('fileUrl', '') // Clear the file URL if a file is selected
    methodsRHF.setValue('selectedFile', file) // Update the form state with the selected file
    methodsRHF.trigger('fileUrl') // Trigger validation for fileUrl (since it's required if there is no selected file)
  }

  const renderSelectValue = (selected: string | null, options: { value: string; label: string }[], selectValue: string) => {
    if (!selected) {
      return <div className='text-[#37A2BA] font-normal'>* {selectValue}</div>
    }
    const selectedOption = options.find((option) => option.value === selected)
    return selectedOption ? selectedOption.label : selected
  }

  return (
    <FormProvider {...methodsRHF}>
      <form>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10'>
          <div className='grid grid-cols-1 gap-3'>
            <CorePersonalFieldsPartial className='grid gap-3' />
            <InputField
              disabled
              id='bookingCategory'
              label='Enquiry reason'
              value={bookingCategory}
            />
            <Controller
              name='bookingClientType'
              control={methodsRHF.control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <Select
                    fullWidth
                    id='BookingClientType'
                    {...field}
                    value={field.value || ''} // Ensure value is always defined
                    onChange={(e) => {
                      field.onChange(e) // Update the form state
                      methodsRHF.trigger('bookingClientType') // Trigger validation for this field
                    }}
                    displayEmpty
                    renderValue={(selected) =>
                      renderSelectValue(selected, [...BOOKING_CLIENT_TYPES], 'Type Of User')
                    }
                    sx={commonSelectStyles}
                  >
                    <MenuItem value={null} disabled>
                      Type Of User
                    </MenuItem>
                    {BOOKING_CLIENT_TYPES.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <SimpleErrorMessage message={error.message} />}
                </div>
              )}
            />
            <Controller
              name='occupation'
              control={methodsRHF.control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <Select
                    id='occupation'
                    fullWidth
                    {...field}
                    value={field.value || ''} // Ensure value is always defined
                    onChange={(e) => {
                      field.onChange(e) // Update the form state
                      methodsRHF.trigger('occupation') // Trigger validation for this field
                    }}
                    displayEmpty
                    renderValue={(selected) =>
                      renderSelectValue(
                        selected,
                        occupations,
                        'Occupation'
                      )
                    }
                    sx={commonSelectStyles}
                    MenuProps={{
                      style: {
                        maxHeight: 400,
                        width: '345px',
                      },
                    }}
                  >
                    <MenuItem value={null} disabled>
                      Occupation
                    </MenuItem>
                    {occupations.map((occupation) => (
                      <MenuItem key={occupation.value} value={occupation.value}
                        sx={{
                          whiteSpace: "unset",
                          wordBreak: "break-all",
                        }}>
                        {occupation.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <SimpleErrorMessage message={error.message} />}
                </div>
              )}
            />
            <Controller
              name='contactMethod'
              control={methodsRHF.control}
              render={({ field, fieldState: { error } }) => (
                <div>
                  <Select
                    fullWidth
                    id='contactMethod'
                    {...field}
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e)
                      methodsRHF.trigger('contactMethod')
                    }}
                    displayEmpty
                    renderValue={(selected) =>
                      renderSelectValue(selected, BOOKING_CONTACT_METHODS, 'Preferred contact method')
                    }
                    sx={commonSelectStyles}
                  >
                    <MenuItem value={null} disabled>
                      Preferred contact method
                    </MenuItem>
                    {BOOKING_CONTACT_METHODS.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        {method.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <SimpleErrorMessage message={error.message} />}
                </div>
              )}
            />
          </div>
          <div className='md:col-start-2'>
            <h5 className='text-black text-xs font-thin md:text-lg md:font-bold leading-none tracking-wide mb-1 md:col-start-2'>
              Your notes
            </h5>
            <Controller
              name='userNotes'
              control={methodsRHF.control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextareaAutosize
                    id='userNotes'
                    className={`min-h-28 ${commonTextareaStyles} ${field.value
                      ? 'text-[#00323D]'
                      : 'rounded-lg text-[#818285]'
                      } `}
                    style={commonShadowStyles}
                    placeholder='Detail reason for booking.'
                    {...field}
                  />
                  {error && <SimpleErrorMessage message={error.message} />}
                </>
              )}
            />
            {bookingCategory === BookingCategory.DOCUMENT_REVIEW && (
              <div className='md:mt-4'>
                <FileSelector
                  selectedFile={methodsRHF.getValues().selectedFile}
                  setSelectedFile={handleFileChange}
                />
                {!selectedFile && (
                  <Controller
                    name="fileUrl"
                    control={methodsRHF.control}
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <>
                          <InputField
                            id="fileUrl"
                            label="File URL"
                            placeholder="File URL"
                            value={field.value || ''}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                          />
                          {error && <SimpleErrorMessage message={error.message} />}
                        </>)
                    }}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {bookingCategory === BookingCategory.LEGAL_ADVICE && (
          <div className='flex flex-col w-full mt-6'>
            <h5 className='text-black text-xs font-thin md:text-lg md:font-bold leading-none tracking-wide mb-1 md:col-start-2'>
              Accessability requirements
            </h5>
            <Controller
              name='accessibilityRequirements'
              control={methodsRHF.control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <TextareaAutosize
                    id='accessibilityRequirements'
                    className={`min-h-24 ${commonTextareaStyles} ${field.value
                      ? 'text-[#00323D]'
                      : 'rounded-lg text-[#818285]'
                      }`}
                    style={commonShadowStyles}
                    placeholder='Detail your accessibility requirements for the meeting.'
                    {...field}
                  />
                  {error && <SimpleErrorMessage message={error.message} />}
                </>
              )}
            />
          </div>
        )}
        <ModalFooter showMandatoryFieldMessage doBeforeNextStep={doValidation} />
      </form>
    </FormProvider >
  )
}
